import React from 'react';
import { MessageCircle, FileText, CheckCircle, Clock, Target, Lightbulb, Star, Repeat, Briefcase, MessageSquare, Zap, Download, BarChart2 } from 'lucide-react';
import LatteIcon from '../components/common/LatteIcon';
import '../styles/desktop/PamphletPage.css';

const PamphletPage = () => {

  const FeatureCard = ({ icon: IconComponent, title, description, highlight = false, isLatte = false }) => (
    <div className={`feature-card ${
      isLatte 
        ? 'border-latte-light bg-gradient-to-br from-latte-lightest to-orange-50 hover:border-latte-primary' 
        : highlight 
        ? 'border-blue-200 bg-blue-50 hover:border-blue-300' 
        : 'border-gray-200 bg-white hover:border-gray-300'
    }`}>
      <div className="flex items-start gap-4">
        <div className={`feature-card-icon ${
          isLatte ? 'bg-latte-light' : highlight ? 'bg-blue-100' : 'bg-gray-100'
        }`}>
          <IconComponent className={`w-6 h-6 ${
            isLatte ? 'text-latte-primary' : highlight ? 'text-blue-600' : 'text-gray-600'
          }`} />
        </div>
        <div>
          <h3 className="feature-card-title">{title}</h3>
          <p className="feature-card-description">{description}</p>
        </div>
      </div>
    </div>
  );

  const StepCard = ({ number, title, description, tips = [] }) => (
    <div className="step-card bg-white border-gray-200">
      <div className="flex items-start gap-4">
        <div className="step-card-number bg-gray-900 text-white">
          {number}
        </div>
        <div className="flex-1">
          <h3 className="step-card-title">{title}</h3>
          <p className="step-card-description">{description}</p>
          {tips.length > 0 && (
            <div className="step-card-tips">
              {tips.map((tip, index) => (
                <div key={index} className="step-card-tip">
                  <Lightbulb className="w-4 h-4 mt-0.5 text-latte-primary" />
                  <span>{tip}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const FAQItem = ({ question, answer }) => (
    <div className="faq-item">
      <h3 className="faq-question">{question}</h3>
      <p className="faq-answer">{answer}</p>
    </div>
  );

  return (
    <div className="pamphlet-page">
      {/* Hero Section from Dashboard */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-subtitle">AI 음성 대화로 시작하는 커리어 컨설팅</div>
          <h1 className="hero-title">
            라떼 한 잔의 시간 동안<br />
            <span>라떼이즈 AI 컨설턴트와 편하게 대화하며</span><br />
            커리어 풀패키지를 완성하세요
          </h1>
          <p className="hero-description">
            단 30분간 음성으로 컨설팅을 진행하면 국문/영문 이력서, 자기소개서, 개인 웹페이지까지 한 번에 생성됩니다
          </p>
          
          <div className="hero-badges">
            <span className="badge">🎙️ 음성 인터뷰</span>
            <span className="badge">📄 프리미엄 이력서 템플릿</span>
            <span className="badge">🌐 영문 이력서</span>
            <span className="badge">🎯 자기소개서</span>
            <span className="badge">🔗 개인 URL 웹페이지 제공</span>
          </div>
        </div>
      </section>

      {/* Latte is Introduction Section */}
      <section className="latte-intro-section">
        <div className="container">
          <div className="latte-intro-wrapper">
            <div className="latte-intro-main">
              <h2 className="latte-intro-title">
                라떼이즈닷컴은 무엇인가요?
              </h2>
              <div className="latte-intro-content">
                <p className="latte-intro-quote">
                  "라떼는 말이지..."
                </p>
                <p className="latte-intro-description">
                  바로 당신의 그 '라떼'가 가장 확실한 '스펙'이 됩니다.<br/>
                  라떼AI가 당신의 모든 경험을 놓치지 않고 가치 있는 데이터로 전환하고,<br/>
                  그 데이터는 곧 당신의 새로운 이력서이자 퍼스널 브랜딩의 핵심 자산이 될 것입니다.
                </p>
              </div>
            </div>
            
            <div className="latte-intro-targets">
              <h3 className="latte-intro-targets-title">이런 분들에게 딱 맞아요</h3>
              <div className="latte-intro-targets-grid">
                <div className="latte-intro-target-item">
                  <div className="latte-intro-target-number">1</div>
                  <p>수많은 경력과 성과를 단순 나열하는 것을 넘어, 하나의 강력한 '성공 서사'로 엮어내고 싶은 분</p>
                </div>
                <div className="latte-intro-target-item">
                  <div className="latte-intro-target-number">2</div>
                  <p>자신의 큰 성과에 대해 "이건 뭐 당연히 한 일이지"라고 생각하며, 그 안에 숨은 비즈니스 가치를 놓치고 있는 분</p>
                </div>
                <div className="latte-intro-target-item">
                  <div className="latte-intro-target-number">3</div>
                  <p>단순 이직이 아닌, 다음 커리어 단계(컨설턴트, 리더, 임원, 창업 등)로의 도약을 위해 자신의 모든 것을 '전략 자산'으로 재구성하고 싶은 분</p>
                </div>
                <div className="latte-intro-target-item">
                  <div className="latte-intro-target-number">4</div>
                  <p>자신만의 노하우와 문제 해결 능력을 이력서에 담아내는 것을 넘어, 후배들에게 영감을 줄 수 있는 블로그나 책등 지식 자산 형태로 남기고 싶은 분</p>
                </div>
              </div>
            </div>
          </div>

          <div className="combined-content">
            <div className="process-section-compact">
              <h2 className="section-title">30분이면 충분합니다</h2>
              <div className="process-grid-compact">
                <div className="process-card-compact">
                  <div className="process-step-number">1</div>
                  <div className="process-content">
                    <h3 className="process-title-compact">대화하기</h3>
                    <p className="process-desc-compact">라떼 컨설턴트에게 당신의 경험을 들려주세요</p>
                  </div>
                </div>
                <div className="process-card-compact">
                  <div className="process-step-number">2</div>
                  <div className="process-content">
                    <h3 className="process-title-compact">실시간 분석</h3>
                    <p className="process-desc-compact">대화 중 이력서가 실시간 자동 완성됩니다</p>
                  </div>
                </div>
                <div className="process-card-compact">
                  <div className="process-step-number">3</div>
                  <div className="process-content">
                    <h3 className="process-title-compact">결과물 제공</h3>
                    <p className="process-desc-compact">프리미엄 템플릿, 자소서, 웹페이지 즉시 생성</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="features-section-compact">
              <h2 className="section-title">라떼이즈, 뭐가 다를까요?</h2>
              <div className="features-grid-compact">
                <div className="feature-card-compact">
                  <div className="feature-icon-wrapper-compact">
                    <FileText className="feature-icon-compact" strokeWidth={1.5} />
                  </div>
                  <div className="feature-content">
                    <h3 className="feature-title-compact">백지에서 시작 가능</h3>
                    <p className="feature-desc-compact">기존 이력서 없어도 OK. 처음부터 라뗴컨설턴트와 함께 만들어 드립니다.</p>
                  </div>
                </div>
                <div className="feature-card-compact">
                  <div className="feature-icon-wrapper-compact">
                    <Repeat className="feature-icon-compact" strokeWidth={1.5} />
                  </div>
                  <div className="feature-content">
                    <h3 className="feature-title-compact">옛날 이야기를 전문성으로 번역</h3>
                    <p className="feature-desc-compact">'라떼는 말이지...'로 시작하는 경험담을 AI가 STAR(Situation, Task, Action, Result) 기법과 정량적 성과로 재구성해드립니다.</p>
                  </div>
                </div>
                <div className="feature-card-compact">
                  <div className="feature-icon-wrapper-compact">
                    <MessageCircle className="feature-icon-compact" strokeWidth={1.5} />
                  </div>
                  <div className="feature-content">
                    <h3 className="feature-title-compact">기억 발굴 중심 대화</h3>
                    <p className="feature-desc-compact">'잘 기억이 안나요'에 대비한 맥락적 기억 회복 질문들로 숨겨진 성과를 찾아드립니다.</p>
                  </div>
                </div>
                <div className="feature-card-compact">
                  <div className="feature-icon-wrapper-compact">
                    <FileText className="feature-icon-compact" strokeWidth={1.5} />
                  </div>
                  <div className="feature-content">
                    <h3 className="feature-title-compact">실시간 이력서 생성</h3>
                    <p className="feature-desc-compact">대화 내용이 실시간으로 이력서에 반영. 완성 되어가는 내 이력서가 즉시 시각적으로 표시됩니다.</p>
                  </div>
                </div>
                <div className="feature-card-compact">
                  <div className="feature-icon-wrapper-compact">
                    <Target className="feature-icon-compact" strokeWidth={1.5} />
                  </div>
                  <div className="feature-content">
                    <h3 className="feature-title-compact">성과 구체화 엔진</h3>
                    <p className="feature-desc-compact">'효율적이었어요'를 '개발 시간 50% 단축'으로, 모호한 표현을 정량적 지표로 변환합니다.</p>
                  </div>
                </div>
                <div className="feature-card-compact">
                  <div className="feature-icon-wrapper-compact">
                    <CheckCircle className="feature-icon-compact" strokeWidth={1.5} />
                  </div>
                  <div className="feature-content">
                    <h3 className="feature-title-compact">Job description 기반 맞춤형</h3>
                    <p className="feature-desc-compact">지원 회사/포지션 및 각자 개인 경험에 맞춘 최적 질문과 맞춤형 결과물을 제공합니다</p>
                  </div>
                </div>
                <div className="feature-card-compact">
                  <div className="feature-icon-wrapper-compact">
                    <Briefcase className="feature-icon-compact" strokeWidth={1.5} />
                  </div>
                  <div className="feature-content">
                    <h3 className="feature-title-compact">4가지 출력 형태</h3>
                    <p className="feature-desc-compact">한 번의 대화로 국문/영문 이력서, JD 맞춤 자기소개서, 개인 웹페이지까지 모두 완성.</p>
                  </div>
                </div>
                <div className="feature-card-compact">
                  <div className="feature-icon-wrapper-compact">
                    <Star className="feature-icon-compact" strokeWidth={1.5} />
                  </div>
                  <div className="feature-content">
                    <h3 className="feature-title-compact">AI 지식 블로그 자동 생성 (9월 오픈)</h3>
                    <p className="feature-desc-compact">대화를 AI가 분석해 블로그 포스트로 자동 변환. 당신의 '라떼'가 후배들을 위한 지식이 됩니다.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Output Types Section from Dashboard */}
      <section className="outputs-section">
        <div className="container">
          <h2 className="section-title">한 번의 대화 = 네 개의 결과물</h2>
          
          <div className="outputs-grid-2x2">
            <div className="output-card-modern">
              <div className="output-preview">
                <img 
                  src="/images/template.png" 
                  alt="PDF 이력서 템플릿 미리보기" 
                  className="template-preview-image"
                />
              </div>
              <p className="output-title-modern">
                프리미엄 이력서 템플릿
              </p>
            </div>
            
            <div className="output-card-modern">
              <div className="output-preview">
                <img 
                  src="/images/eng.png" 
                  alt="영어 이력서 미리보기" 
                  className="template-preview-image"
                />
              </div>
              <p className="output-title-modern">
                영문 이력서
              </p>
            </div>
            
            <div className="output-card-modern">
              <div className="output-preview">
                <img 
                  src="/images/coverletter.png" 
                  alt="JD 맞춤 자소서 미리보기" 
                  className="template-preview-image"
                />
              </div>
              <p className="output-title-modern">
                JD 맞춤 자소서
              </p>
            </div>
            
            <div className="output-card-modern">
              <div className="output-preview">
                <img 
                  src="/images/webpage.png" 
                  alt="개인 웹페이지 미리보기" 
                  className="template-preview-image webpage-preview"
                />
              </div>
              <p className="output-title-modern">
                개인 웹페이지
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section from Dashboard */}
      <section className="testimonials-section">
        <div className="container">
          <h2 className="section-title">라떼 컨설턴트를 만난 사람들의 찐 후기</h2>
          
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-stars">⭐⭐⭐⭐⭐</div>
              <p className="testimonial-content">
                "진짜 커피 한 잔 마시면서 옛날 이야기하듯 편하게 말했을 뿐인데, 그 동안 성과라고 생각해보지 못했던 내용들까지 기억나게 해주고 그걸 모두 정량화 한 가치로 바꿔주니 정말 그럴 듯한 이력서가 만들어지더라구요."
              </p>
              <div className="testimonial-author">
                <strong>L사</strong> · 14년차 개발자 김OO님
              </div>
            </div>
            
            <div className="testimonial-card">
              <div className="testimonial-stars">⭐⭐⭐⭐⭐</div>
              <p className="testimonial-content">
                "그냥 푸념하듯 얘기한 야근 스토리가 '위기관리 능력'과 '프로젝트 리더십'으로 정리되는 걸 보고 놀랐어요. 저 혼자는 찾아낼 수 없는 제 찐 가치였어요"
              </p>
              <div className="testimonial-author">
                <strong>S전자</strong> · 15년차 기획자 이OO님
              </div>
            </div>
            
            <div className="testimonial-card">
              <div className="testimonial-stars">⭐⭐⭐⭐⭐</div>
              <p className="testimonial-content">
                "채용공고 보고 지원할 때마다 자소서 처음부터 다시 쓰느라 힘들었는데, 라떼매니저가 채용공고 분석해서 '이 회사는 데이터 기반 의사결정을 중요시하네요', '협업 경험을 강조하세요' 이렇게 맞춤 질문을 해주니까 답변하기만 하면 자소서가 완성되더라구요. 서류 합격률이 3배는 올라간 것 같아요!"
              </p>
              <div className="testimonial-author">
                <strong>T커머스</strong> · 3년차 마케터 송OO님
              </div>
            </div>
            
            <div className="testimonial-card">
              <div className="testimonial-stars">⭐⭐⭐⭐⭐</div>
              <p className="testimonial-content">
                "30년간 조선소에서 일했는데 이력서를 어떻게 써야 할지 막막했어요. 그냥 '배 만들고 팔았다'고만 생각했는데... 라떼 컨설턴트랑 대화하니까 'LNG선 수주 300억 달러' 이런 표현으로 재정리되더라구요."
              </p>
              <div className="testimonial-author">
                <strong>S중공업</strong> · 30년차 조선 기술영업 부장 한OO님
              </div>
            </div>
            
            <div className="testimonial-card">
              <div className="testimonial-stars">⭐⭐⭐⭐⭐</div>
              <p className="testimonial-content">
                "'그 스타트업 왜 투자했어?' 물으면 그냥 '감이 좋았다'고만 했었죠. 라떼매니저가 '그때 어떤 지표를 봤나요?' '경쟁사 대비 뭐가 달랐나요?' 계속 물어봐서 대답하다 보니... '시장 성장률 연 47% 분석', '경쟁사 대비 CAC 73% 절감' 이런 구체적인 숫자들이 정리 되었어요!"
              </p>
              <div className="testimonial-author">
                <strong>B투자사</strong> · 10년차 VC 심사역 윤OO님
              </div>
            </div>
            
            <div className="testimonial-card">
              <div className="testimonial-stars">⭐⭐⭐⭐⭐</div>
              <p className="testimonial-content">
                "처음엔 그냥 이력서 정리만 하려고 했는데, 30분 대화 끝나니까 영문 이력서는 물론이고 제 웹페이지까지 바로 만들 수 있더라구요. 득템한 기분이었어요~"
              </p>
              <div className="testimonial-author">
                <strong>N사</strong> · 6년차 데이터분석가 강OO님
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* User Guide Content */}
      <section className="guide-section">
        <div className="container">
          {/* 사용 방법 */}
          <div className="mb-16">
            <h2 className="section-title">이용 방법</h2>
            <div className="space-y-8">
              <StepCard
                number="1"
                title="회원가입 및 컨설팅 시작"
                description="구글 또는 개인 이메일 로그인 후 '컨설팅 시작하기' 버튼을 클릭만하면 바로 컨설팅이 시작됩니다."
              />
              <StepCard
                number="2"
                title="자연스러운 경험담 대화"
                description="라떼 컨설턴트 질문에 따라 편안하게 당신의 경력을 들려주세요. 라떼 AI가 경청하며 숨겨진 전문성을 찾아드립니다. 지원하길 원하는 회사의 포지션이나 자격요건을 알려주시면 그에 맞는 경험으로 더 최적화 된 컨설팅이 진행됩니다"
                tips={[
                  "그 때 그 상황들을 구체적으로 이야기해주시면 좋아요",
                  "어려웠던 상황일수록 좋은 성과 포인트가 될 수 있어요"
                ]}
              />
              <StepCard
                number="3"
                title="실시간 이력서 확인"
                description="우측 화면에서 컨설팅 내용이 실시간으로 이력서에 반영되는 것을 확인하세요. 업데이트되는 부분이 초록색으로 하이라이트됩니다."
              />
              <StepCard
                number="4"
                title="이력서 편집"
                description="'이력서 보기' 버튼으로 편집 페이지에서 세부 내용을 직접 수정할 수 있습니다. 라떼 AI가 작성한 내용을 기반으로 사용자에게 맞게 커스터마이징하세요."
                tips={["편집 내용은 자동 저장되어 언제든 다시 수정 가능합니다"]}
              />
              <StepCard
                number="5"
                title="다양한 형태로 활용하기"
                description="완성된 이력서를 4가지 형태로 즉시 활용할 수 있습니다. 프리미엄 템플릿을 적용하여 즉시 PDF 다운로드하고, AI가 전문적인 영어 이력서로 번역하여 글로벌 기업 지원 시 활용하며, JD 맞춤 자소서를 생성하고, 나만의 URL로 온라인 이력서 페이지를 생성하여 링크로 공유할 수 있습니다."
              />
            </div>
          </div>


          {/* FAQ */}
          <div className="mb-16">
            <h2 className="section-title">자주 묻는 질문</h2>
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <FAQItem
                question="구체적으로 기억이 안 나는 부분이 있어도 괜찮을까요?"
                answer="전혀 문제없습니다! 그 뒤에 숨어있는 전문 지식과 노하우를 라떼 AI가 알아서 찾아드립니다. 중요한 건 솔직한 경험담입니다."
              />
              <FAQItem
                question="컨설팅은 얼마나 오래 걸리나요?"
                answer="평균 25-30분 정도입니다. 경력이 많으실수록 조금 더 오래 걸릴 수 있어요. 중간에 언제든 저장하고 나중에 이어서 할 수 있습니다."
              />
              <FAQItem
                question="기존에 작성해 놓은 이력서를 활용할 수 있나요?"
                answer="물론입니다! 기존 이력서를 라떼 컨설턴트에게 복사/붙여넣기 해주시면 그 내용을 기반으로 보완할 내용들을 추가로 컨설팅해드립니다."
              />
              <FAQItem
                question="음성 인식이 잘못될 때는 어떻게 하나요?"
                answer="라떼 AI가 문맥을 파악해서 자동으로 수정하거나, 텍스트로 다시 입력하시면 됩니다. 중요한 내용은 텍스트로 보완 설명하시는 것을 추천합니다."
              />
              <FAQItem
                question="개인정보는 안전한가요?"
                answer="모든 데이터는 암호화되어 저장되며, 오직 이력서 작성 목적으로만 사용됩니다. 사용자의 동의 없이 다른 용도로 사용되지 않습니다."
              />
              <FAQItem
                question="수정 후 다시 PDF를 받을 수 있나요?"
                answer="네, 언제든지 수정 후 재다운로드 가능합니다. '이전 이력서 목록'에서 모든 버전을 관리할 수 있어요."
              />
              <FAQItem
                question="영문 이력서는 어떻게 만드나요?"
                answer="한국어 이력서 완성 후 '영어 이력서 변환' 버튼만 클릭하면 AI가 전문적인 영문 이력서로 즉시 번역해드립니다. 글로벌 기업 지원이나 해외 진출 시 바로 활용 가능합니다."
              />
              <FAQItem
                question="웹페이지 URL은 어떻게 설정하나요?"
                answer="웹페이지 생성 시 원하는 주소를 설정할 수 있습니다. https://latteis.com/{yourname} 형태로 생성되며, 이 링크를 채용담당자나 동료들과 공유하면 언제든 온라인에서 이력서를 확인할 수 있습니다."
              />
              <FAQItem
                question="AI 지식 블로그는 어떻게 작동하나요?(9월 오픈)"
                answer="이력서 컨설팅에서 나눈 30분의 대화를 AI가 분석해 여러 개의 블로그 포스트로 자동 변환합니다. 예를 들어 '신입 시절 야근 스토리'가 '주니어 개발자의 성장기'라는 포스트로, '팀 리드 경험'이 '효과적인 팀 리딩 노하우'라는 포스트로 변환됩니다. 각 포스트마다 음성으로 추가 이야기를 계속 더해가며 콘텐츠를 풍부하게 만들 수 있습니다."
              />
            </div>
          </div>
        </div>
      </section>
      
    </div>
  );
};

export default PamphletPage;